/// <reference lib="WebWorker" />

// Require all dayjs locales
import 'dayjs/locale/en.js';
import 'dayjs/locale/es.js';
import 'dayjs/locale/de.js';
import 'dayjs/locale/fr.js';
import 'dayjs/locale/it.js';
import 'dayjs/locale/pt.js';
import { RemixBrowser, useLocation, useMatches } from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import dayjs from 'dayjs';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { StrictMode, startTransition, useEffect } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { getInitialNamespaces } from 'remix-i18next/client';

import { getAppGlobals } from './components/AppGlobals/utils';
import i18n from './i18n';

// Save the original fetch function
const originalFetch = window.fetch;

// Override the fetch function
window.fetch = async (input, options = {}) => {
  function isRemixDataRequest(url: URL) {
    return url.searchParams.has('_data');
  }
  // Normalize input to a Request object
  let request: Request;
  if (input instanceof Request) {
    // Merge options into the existing Request
    const headers = new Headers(input.headers);
    if (options.headers) {
      new Headers(options.headers).forEach((value, key) => {
        headers.set(key, value);
      });
    }
    request = new Request(input, { ...options, headers });
  } else {
    // input is string or URL
    request = new Request(input, options);
  }
  const u = new URL(request.url, window.location.href);
  // If the request is a same-origin request, we can add custom headers
  if (u.origin === window.location.origin) {
    const headers = new Headers(request.headers);

    // check if crypto is available (only in secure contexts)
    if ('crypto' in window && crypto.randomUUID) {
      const correlationId = crypto.randomUUID();
      headers.set('x-correlation-id', correlationId);
      Sentry.setTag('x-correlation-id', correlationId);
    }

    if (isRemixDataRequest(u)) {
      // Ensure accept header exists
      if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
      }
      const modifiedRequest = new Request(request, { headers });
      const response = await originalFetch(modifiedRequest);
      // Check if the response is an error but did not come from Remix
      // Cloudflare/HUMAN intercepts Remix calls and sometimes returns a 400+
      // status code. Since it should have hit Remix, we need to add
      // the remix response header so client side code can handle it
      if (
        response.status >= 400 &&
        response.headers.get('x-remix-response') === null &&
        response.headers.get('x-remix-error') === null &&
        response.headers.get('x-remix-catch') === null
      ) {
        const headers = new Headers(response.headers);
        headers.set('x-remix-response', 'yes');
        // Create a new response with the modified headers
        const modifiedResponse = new Response(response.body, {
          status: response.status,
          statusText: response.statusText,
          headers: headers,
        });

        return modifiedResponse;
      }
      return response;
    }
    // Call the original fetch with the modified headers
    const modifiedRequest = new Request(request, { headers });
    return originalFetch(modifiedRequest);
  }
  // Call the original fetch
  return originalFetch(input, options);
};

// This function is called when the HUMAN captcha is successfully completed
window._pxOnCaptchaSuccess = (isValid: boolean) => {
  console.log('Captcha success', isValid);
};

// add polyfill for Object.hasOwn [BEE-7367]
if (!Object.hasOwn) {
  Object.hasOwn = (obj, prop) => Object.prototype.hasOwnProperty.call(obj, prop);
}

// add a boolean to the window object to enable logging of sentry events
// to console for debugging purposes
declare global {
  interface Window {
    __BEEHIIV_CONSOLE_LOG_SENTRY_EVENT__: boolean;
    _pxOnCaptchaSuccess: (isValid: boolean) => void;
  }
}

const appGlobals = getAppGlobals();

try {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .register(`/entry.worker.js?v=${appGlobals.RELEASE_VERSION}`, {
        type: 'module',
        scope: '/',
      })
      .catch((error) => {
        // if there's an error registering the service worker, mostly due to a 403 error,
        // from Cloudflare, there's not much we can do, so we just log the error
        // and continue without the service worker
        console.error('Service Worker registration failed:', error);
      });
  }
} catch (error) {
  // If the service worker registration fails, we log the error but continue
  // without the service worker. This is important for environments where service workers
  // are not supported or when the registration fails due to network issues.
  console.error('Service Worker registration failed:', error);
}

Sentry.init({
  dsn: appGlobals.SENTRY_CLIENT_DSN,
  environment: appGlobals.SENTRY_ENV,
  release: appGlobals.SENTRY_RELEASE || 'local',
  tracesSampleRate: 0, // disable performance sampling
  replaysSessionSampleRate: 0, // disable session replay
  replaysOnErrorSampleRate: 0,
  beforeSend(event, _hint) {
    // Check if error boundary was triggered
    const isRemixErrorBoundary = event?.tags && event?.tags?.keyword === 'remix-error-boundary';

    // Check if the event was from our app code
    // Since the error occurs in the browser, we can't rely on the sourcemap
    // So we check if the filename is from the /assets folder
    const inApp = event.exception?.values?.some((e) =>
      e.stacktrace?.frames?.some((f) => f.filename?.includes('/assets/')),
    );

    // Exclude certain patterns from being sent to sentry
    // Array of regex patterns to match against the abs_path of the frame
    const excludePatterns = [/googletagmanager\.com\/gtm\.js/];
    const excluded = event.exception?.values?.some((e) =>
      e.stacktrace?.frames?.some((f) => excludePatterns.some((p) => f.abs_path && p.test(f.abs_path))),
    );

    // Don't clog up slack sentry with development errors
    // Or errors that did not originate from our app or excluded
    if (process.env.NODE_ENV === 'development' || !isRemixErrorBoundary || !inApp || excluded) {
      return null;
    }

    return event;
  },

  integrations: [
    Sentry.browserTracingIntegration({
      useEffect,
      useLocation,
      useMatches,
    }),
    Sentry.replayIntegration(),
  ],
});

async function hydrate() {
  await i18next
    .use(initReactI18next) // Tell i18next to use the react-i18next plugin
    .use(LanguageDetector) // Setup a client-side language detector
    .use(Backend) // Setup your backend
    .init({
      ...i18n, // spread the configuration
      // This function detects the namespaces your routes rendered while SSR use
      ns: getInitialNamespaces(),
      backend: { loadPath: '/locales/{{lng}}/{{ns}}.json' },
      detection: {
        // Here only enable htmlTag detection, we'll detect the language only
        // server-side with remix-i18next, by using the `<html lang>` attribute
        // we can communicate to the client the language detected server-side
        order: ['htmlTag'],
        // Because we only use htmlTag, there's no reason to cache the language
        // on the browser, so we disable it
        caches: [],
      },
    });

  startTransition(() => {
    hydrateRoot(
      document,
      <I18nextProvider i18n={i18next}>
        <StrictMode>
          <RemixBrowser />
        </StrictMode>
      </I18nextProvider>,
    );
  });
}

dayjs.locale(i18next.language);

if (window.requestIdleCallback) {
  window.requestIdleCallback(hydrate);
} else {
  // Safari doesn't support requestIdleCallback
  // https://caniuse.com/requestidlecallback
  window.setTimeout(hydrate, 1);
}
